import { useContext, createContext, PropsWithChildren, useMemo } from "react"

import { UUID } from "io-ts-types/lib/UUID"
import { Maybe } from "@flockcover/flock-svc-core-types"
import { unwrap } from "@shared/utils/helpers/unwrap"
import makeUUID from "@shared/utils/helpers/makeUUID"
import { useAuth } from "../../../contexts/AuthProvider"

type ViewerContextData = Readonly<{
  viewer: Maybe<UUID>
}>

const ViewerContext = createContext<ViewerContextData | undefined>(undefined)

export const ViewerProvider = ({ children }: PropsWithChildren<{}>) => {
  const authService = useAuth()

  const user = unwrap(authService.getAuthUser())

  const value = useMemo(
    () => ({
      viewer: [`distributor`, `claims_admin`, `carrier`].includes(user.type) ? makeUUID(unwrap(user.ownerId)) : null,
    }),
    []
  )

  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>
}

export function useViewer() {
  const context = useContext(ViewerContext)

  if (context === undefined) {
    throw new Error(`useViewer must be used inside ViewerProvider.`)
  }

  return context
}

type MockViewerProviderProps = {
  viewer?: UUID
}

export const MockViewerProvider = ({ viewer, children }: PropsWithChildren<MockViewerProviderProps>) => {
  const value = useMemo(
    () => ({
      viewer: viewer ?? null,
    }),
    [viewer]
  )
  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>
}

MockViewerProvider.defaultProps = {
  viewer: null,
}
